import { PencilLight } from '@commenda/icons';
import { Address } from '@commenda/prisma';
import { IncorporationRequestDataType } from '@commenda/types';
import { SupportedEntityType } from '@commenda/types/src/corporation';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getCorporationNameFromWorkRequest } from '@commenda/utils';
import { Country, State } from 'country-state-city';
import { SummaryFieldKS } from '../common/SummaryFieldKS';
import { fundingOptions, reasonsForOpeningInSG } from './common';

const formatAddress = (address: Address) => {
  return [
    `${address?.addressLine1}${address?.addressLine2 ? `, ${address.addressLine2}` : ''}`,
    `${address?.city} - ${address?.postalCode}`,
    `${
      State.getStateByCodeAndCountry(address?.state, address?.country)?.name
    }, ${Country.getCountryByCode(address?.country)?.name}`,
  ];
};

type SummaryCompanyInfoProps = {
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  hideActionButtons?: boolean;
  setActiveStep: (itemIndex: number) => void;
  currentCorporationDetails: SupportedEntityType;
};

export const SummaryCompanyInfo = ({
  data,
  hideActionButtons,
  setActiveStep,
  currentCorporationDetails,
}: SummaryCompanyInfoProps) => {
  const countriesOfBusiness = data?.requestData?.supportedCountries
    ? data?.requestData?.supportedCountries
        .split(',')
        .map((c) => Country.getCountryByCode(c)?.name)
    : [];

  const countriesAsString = countriesOfBusiness.join(', ');
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4
          className="body-16-bold text-text-primary
        "
        >
          Company Info
        </h4>
        {hideActionButtons ? null : (
          <PencilLight
            className="cursor-pointer size-5"
            onClick={() => setActiveStep(0)}
          />
        )}
      </div>
      <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-lg">
        <div className="flex flex-col gap-2 p-6">
          <SummaryFieldKS
            heading="Entity Name 1"
            value={getCorporationNameFromWorkRequest(data?.requestData)}
          />
          <SummaryFieldKS
            heading="Entity Name 2"
            value={getCorporationNameFromWorkRequest(
              data?.requestData,
              'companyLegalName2'
            )}
          />
          <SummaryFieldKS
            heading="Entity Name 3"
            value={getCorporationNameFromWorkRequest(
              data?.requestData,
              'companyLegalName3'
            )}
          />
          <SummaryFieldKS
            heading="Corporation Type"
            value={currentCorporationDetails?.appCopy?.corporationTypeShortName}
          />

          {data?.requestData?.socialLink ? (
            <SummaryFieldKS
              heading="Company website"
              value={data?.requestData?.socialLink}
            />
          ) : null}

          <SummaryFieldKS
            heading="Primary SSIC"
            value={data?.requestData?.primarySSICCode}
          />

          <SummaryFieldKS
            heading="Secondary SSIC"
            value={data?.requestData?.secondarySSICCode}
          />

          <SummaryFieldKS
            heading="Description"
            value={data?.requestData?.businessDescription}
          />

          <SummaryFieldKS
            heading="Involved in crypto"
            value={
              data?.requestData?.isBusinessInvolvedInCryptoCurrency
                ? 'Yes'
                : 'No'
            }
          />

          <SummaryFieldKS
            heading="Accepts crypto payments"
            value={
              data?.requestData?.isBusinessAcceptingCryptocurrencyPayments
                ? 'Yes'
                : 'No'
            }
          />

          <SummaryFieldKS
            heading="Provides crypto services"
            value={
              data?.requestData?.doesBusinessProvideServicesToCryptoCompanies
                ? 'Yes'
                : 'No'
            }
          />

          <SummaryFieldKS
            heading="Operates or manages crypto"
            value={
              data?.requestData?.isBusinessManagingCryptocurrencyCoins
                ? 'Yes'
                : 'No'
            }
          />

          {data?.requestData?.hasOtherReasonForInvolvementInCrypto ? (
            <SummaryFieldKS
              heading="Reason for involvement in crypto"
              value={data?.requestData?.reasonForInvolvementInCrypto}
            />
          ) : null}

          <SummaryFieldKS
            heading="Company funding"
            value={
              fundingOptions.find(
                (o) => o.value === data?.requestData?.howWillBusinessBeFunded
              ).label
            }
          />

          <SummaryFieldKS
            heading="Purpose of incorporating in Singapore"
            value={
              data?.requestData?.reasonForIncorporatingInSingapore === 'Other'
                ? data?.requestData?.customReasonForIncorporatingInSingapore
                : reasonsForOpeningInSG.find(
                    (p) =>
                      p.value ===
                      data?.requestData?.reasonForIncorporatingInSingapore
                  )?.label
            }
          />

          {countriesAsString ? (
            <SummaryFieldKS
              heading="Preset or future places of business"
              value={countriesAsString}
            />
          ) : null}

          <SummaryFieldKS
            heading="Physical address"
            value={
              data?.requestData?.hasOptedForSGRegisteredAddress
                ? "Using Commenda's managed address service"
                : formatAddress(
                    data?.addresses?.find(
                      (add) => add.type === 'OperatingAddress'
                    )?.value
                  )
            }
          />

          {data?.requestData?.hasOptedForSGMailingAddress ||
          data?.addresses?.find((add) => add.type === 'MailingAddress')
            ?.value ? (
            <SummaryFieldKS
              heading="Mailing address"
              value={
                data?.requestData?.hasOptedForSGMailingAddress
                  ? formatAddress(
                      data?.addresses?.find(
                        (add) => add.type === 'OperatingAddress'
                      )?.value
                    )
                  : formatAddress(
                      data?.addresses?.find(
                        (add) => add.type === 'MailingAddress'
                      )?.value
                    )
              }
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
